import {AnnoTypeEnum, ScribbleAnnotation, Size} from '@mydocma/component-library/src/lib/annotation';
import {Scribble, ScribbleData} from '@docma-mm-core/scribble/scribble';
import {PinStyleEnum} from '@docma-mm-core/scribble/pin-style.enum';
import {toARGB, toRGB} from './converter';

export class ScribbleConverter {

  /* COMPONENT MODEL TO SCRIBBLE */
  static convertToData(anno: ScribbleAnnotation, containerSize: Size): ScribbleData {
    const points = anno.points;
    const xs = points.filter((_, i) => i % 2 === 0);
    const ys = points.filter((_, i) => i % 2 === 1);
    const strokeWidth = anno.style?.stroke?.width ?? 3;
    const halfStroke = strokeWidth / 2;
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const adjustedMinX = minX - halfStroke;
    const adjustedMaxX = maxX + halfStroke;
    const adjustedMinY = minY - halfStroke;
    const adjustedMaxY = maxY + halfStroke;

    const width = adjustedMaxX - adjustedMinX;
    const height = adjustedMaxY - adjustedMinY;

    const shiftedPoints: number[] = [];
    for (let i = 0; i < points.length; i += 2) {
      shiftedPoints.push(Math.round(points[i] - adjustedMinX));
      shiftedPoints.push(Math.round(points[i + 1] - adjustedMinY));
    }
    // Convert array of numbers into pairs of "x,y" strings
    const pointPairs = [];
    for (let i = 0; i < shiftedPoints.length; i += 2) {
      pointPairs.push(`${shiftedPoints[i]},${shiftedPoints[i + 1]}`);
    }

    const stageWidth = containerSize.width;
    const stageHeight = containerSize.height;

    const relW = width / stageWidth;
    const relH = height / stageHeight;
    const relX = (adjustedMinX / stageWidth) + relW / 2;
    const relY = (adjustedMinY / stageHeight) + relH / 2;

    const data: ScribbleData = {
      UniqueId: anno.id,
      RelativeBasePointToParentPositionX: relX,
      RelativeBasePointToParentPositionY: relY,
      RelativeScaleX: relW,
      RelativeScaleY: relH,

      RelativeAnchorToParentPositionX: relX,
      RelativeAnchorToParentPositionY: relY,
      RelativeAnchorToCanvasPositionX: 0.5,
      RelativeAnchorToCanvasPositionY: 0.5,

      ScribblePath: pointPairs.join(' '), // Format: "x1,y1 x2,y2 x3,y3"
      PinShape: anno.type,
      PinStyle: PinStyleEnum.Scribble,

      BorderColorValue: toARGB(anno.style?.stroke?.color, anno.style?.stroke?.opacity),
      BorderWidth: anno.style?.stroke?.width ?? 3,
      FillColorValue: '#00FFFFFF',
    };
    return data;
  }

  /* SCRIBBLE TO COMPONENT MODEL */
  static convertToModel(scribble: ScribbleData, container: Size): ScribbleAnnotation {
    const anno = {
      type: scribble.PinShape,
      id: scribble.UniqueId,
      points: [],
      style: {
        stroke: {
          color: toRGB(scribble.BorderColorValue),
          width: scribble.BorderWidth ?? 3,
          opacity: scribble.PinShape === AnnoTypeEnum.Marker ? 0.7 : 1  // scribble.strokeOpacity
        }
      }
    } as ScribbleAnnotation;

    if (scribble.ScribblePath) {
      const width = (scribble.RelativeScaleX || 0) * container.width;
      const left = container.width * scribble.RelativeBasePointToParentPositionX - (scribble.RelativeAnchorToCanvasPositionX || 0) * width;
      const height = (scribble.RelativeScaleY || 0) * container.height;
      const top = container.height * scribble.RelativeBasePointToParentPositionY - (scribble.RelativeAnchorToCanvasPositionY || 0) * height;

      const points: number[] = scribble.ScribblePath.split(/[\s,]+/).map((p, i) => {
        if (i % 2) {
          return top + Number(p);
        } else {
          return left + Number(p);
        }
      });
      anno.points = points;
    }
    return anno;
  }
}
